import { motion, AnimatePresence } from 'framer-motion';
import type { CreatedOrder } from '../services/api';
import {
  CreditCard,
  ShoppingBag,
  Info,
  X,
  Mail,
} from 'lucide-react';

interface OrderSuccessModalProps {
  isOpen: boolean;
  order: CreatedOrder | null;
  onClose: () => void;
}

export default function OrderSuccessModal({
  isOpen,
  order,
  onClose,
}: OrderSuccessModalProps) {
  if (!order) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-workshop-surface w-full max-w-lg rounded-sm shadow-2xl overflow-hidden border border-white/[0.08] relative">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-1.5 text-text-muted hover:text-cream
                           hover:bg-white/[0.06] rounded-sm transition-colors"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Header */}
              <div className="relative bg-workshop px-6 py-8 text-center border-b border-white/[0.06] overflow-hidden">
                {/* Decorative glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-accent/10 rounded-full blur-3xl" />

                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 12 }}
                  className="relative w-20 h-20 mx-auto mb-4"
                >
                  <div className="absolute inset-0 rounded-full bg-accent/20" />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="absolute inset-0 rounded-full bg-accent/10"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center">
                      <svg
                        className="w-7 h-7 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <motion.path
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ delay: 0.4, duration: 0.5 }}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>
                </motion.div>

                <h2 className="relative text-2xl font-display text-accent tracking-wide">
                  ORDER SUBMITTED
                </h2>
                <p className="relative text-text-muted mt-2 text-sm">
                  Your wholesale order has been created successfully
                </p>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Order Details */}
                <div className="bg-workshop rounded-sm overflow-hidden border border-white/[0.06] mb-6">
                  <div className="divide-y divide-white/[0.06]">
                    <div className="flex justify-between items-center px-4 py-3">
                      <span className="text-text-muted uppercase text-[11px] tracking-wider font-medium">
                        Order Number
                      </span>
                      <span className="font-semibold text-cream text-sm">{order.name}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-3">
                      <span className="text-text-muted uppercase text-[11px] tracking-wider font-medium">
                        Items
                      </span>
                      <span className="font-medium text-cream text-sm flex items-center gap-1.5">
                        <ShoppingBag className="w-3.5 h-3.5 text-text-muted" />
                        {order.lineItems.length} product{order.lineItems.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-3">
                      <span className="text-text-muted uppercase text-[11px] tracking-wider font-medium">
                        Order Total
                      </span>
                      <span className="text-xl font-bold text-accent tabular-nums">
                        ${parseFloat(order.totalPrice).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Line Items Preview */}
                {order.lineItems.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-[11px] uppercase tracking-wider text-text-muted mb-2 font-medium">
                      Order Summary
                    </h3>
                    <div className="bg-workshop rounded-sm border border-white/[0.06] divide-y divide-white/[0.04] max-h-40 overflow-y-auto">
                      {order.lineItems.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                          className="px-4 py-2.5 flex justify-between items-center text-sm"
                        >
                          <span className="truncate flex-1 mr-4 text-cream">
                            {item.title}
                            <span className="text-text-muted ml-1.5 text-xs bg-white/[0.04] px-1.5 py-0.5 rounded-sm">
                              x{item.quantity}
                            </span>
                          </span>
                          <span className="font-medium text-accent tabular-nums">
                            ${(parseFloat(item.discountedPrice) * item.quantity).toFixed(2)}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invoice Notice */}
                <div className="bg-accent/[0.08] border border-accent/20 rounded-sm p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-accent font-medium">Invoice Ready for Payment</p>
                      <p className="text-xs text-text-muted mt-1 leading-relaxed">
                        Click the button below to complete your order. You can also
                        find this invoice link in your email.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2.5">
                  <motion.a
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    href={order.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-accent w-full flex items-center justify-center gap-2 text-center"
                  >
                    <CreditCard className="w-4 h-4" />
                    PAY INVOICE NOW
                  </motion.a>
                  <button
                    onClick={onClose}
                    className="btn-secondary w-full"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-workshop px-6 py-3 text-center border-t border-white/[0.06]">
                <p className="text-xs text-text-muted flex items-center justify-center gap-1.5">
                  <Mail className="w-3 h-3" />
                  Questions?{' '}
                  <a
                    href="mailto:wholesale@topdogbrand.com"
                    className="text-accent hover:text-accent-light underline underline-offset-2"
                  >
                    Contact our wholesale team
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
